import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { gql } from "apollo-boost";
import decode from "jwt-decode";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import CryptoJS from "crypto-js";
import classNames from "classnames";

import { withModal, Button } from "components/common";
import TerminationModal from "components/Popups/Modals/TerminationModal";
import { UPDATE_AMBASSADOR_STATUS } from "utils";
import { ERROR_STATUS, DAYS_OF_WEEK } from "utils/constants";
import { ProductPill } from "components/common/ProductPills";
import { formatBirthdateForUI } from "../../../../utils/helpers";
import {
  SET_CREDIT_LIMIT,
  FETCH_BALANCES,
  RESOLVE_LIMIT_REQUEST,
  LOST_CARD_RESEND,
  SET_USER_TAGS,
  RESOLVE_FRAUD_TAG,
  FREEZE_USER_CARD,
  UNFREEZE_USER_CARD,
  SUBMIT_DISPUTE,
  REMOVE_USER_TRANSACTION_TAG,
  ADD_USER_TRANSACTION_TAGS,
} from "../../../../utils/mutations";
import { track } from "../../../../utils/analytics";
import UserTransactionsTable from "./UserTransactionsTable";
import UserPaymentsTable from "./UserPaymentsTable";
import UserScheduledPaymentsTable from "./UserScheduledPaymentsTable";
import AssetReportTable from "./AssetReportTable";
import UnderwritingDataBox from "./UnderwritingDataBox";
import AccountsList from "./AccountsList";
import BankIdentityDataBox from "./BankIdentityDataBox";
import FraudDataBox from "./FraudDataBox";
import Notes from "./Notes";
import IpAddressTable from "./IpAddressTable";
import QUERY from "./query";
import UnderwritingDecisionDataBox from "./UnderwritingDecisionDataBox";

const AMBASSADOR_TYPE = { AMBASSADOR: "ambassador", NORMAL: "normal" };
const MAKE_AMBASSADOR = "Make Ambassador";
const REMOVE_AMBASSADOR = "Remove Ambassador";

const UserStats = (props) => {
  const {
    user,
    setCreditLimit,
    openModal,
    lostCard,
    fetchBalances,
    balancesLoading,
    setUserTags,
    resolveFraudTag,
    freezeUserCard,
    unfreezeUserCard,
    refetch,
  } = props;
  const { locked_reasons } = JSON.parse(user.card.metadata || "{}");
  const [cardStatus, setCardStatus] = useState(user.card.status);
  const [lockReasons, setLockReasons] = useState(
    Object.keys(locked_reasons || []).map((el) =>
      (el === "fraud_alert" ? locked_reasons.fraud_alert.reason : el),
    ),
  );
  const [last4SSN, setLast4SSN] = useState("");
  const [ssnResult, setSSNResult] = useState(null);
  const [shouldShowSSNCheck, setShouldShowSSNCheck] = useState(false);
  const [shouldShowTermination, setShouldShowTermination] = useState(false);
  const [phoneUpdated, setPhoneUpdated] = useState(false);
  const [newNum, setNewNum] = useState("");
  const [dOBUpdated, setDOBUpdated] = useState(false);
  const [newDOB, setNewDOB] = useState("");
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(
    user.payment_methods.find((pm) => pm.id === user.default_payment_method.id) ||
      user.payment_methods[0],
  );
  const [ambassadorText, setAmbassadorText] = useState(
    user.type === AMBASSADOR_TYPE.AMBASSADOR
      ? REMOVE_AMBASSADOR
      : MAKE_AMBASSADOR,
  );
  const [ambassadorButtonDisabled, setAmbassadorButtonDisabled] = useState(
    false,
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [updateAmbassadorStatus] = useMutation(UPDATE_AMBASSADOR_STATUS);

  useEffect(() => {
    if (last4SSN.length === 4) {
      checkSSN();
    } else { setShouldShowSSNCheck(false); }
  }, [last4SSN]);

  useEffect(() => {
    try {
      const token = localStorage.getItem("jwt") || "";
      const { canTerminate } = decode(token);
      if (user.completion_status !== "terminated") {
        setShouldShowTermination(canTerminate);
      }
    } catch (adminError) {
      setShouldShowTermination(false);
    }
  }, []);

  const totalTransactionVolume = user.transactions.reduce(
    (total, t) =>
      (total += !["CANCELED"].includes(t.status) && t.amount ? t.amount : 0),
    0,
  );
  const totalSettledTransactionVolume = user.transactions.reduce(
    (total, t) =>
      (total += ["SETTLED"].includes(t.status) && t.amount ? t.amount : 0),
    0,
  );
  const totalPendingTransactionVolume = user.transactions.reduce(
    (total, t) =>
      (total += ["PENDING"].includes(t.status) && t.amount ? t.amount : 0),
    0,
  );
  const totalPaymentVolume = user.payments.reduce(
    (total, p) =>
      (total +=
        !["RETURNED", "CANCELED"].includes(p.status) && p.amount
          ? p.amount
          : 0),
    0,
  );

  const totalScheduledPaymentVolume = user.scheduledPayments.reduce(
    (total, p) =>
      (total +=
        p.status === 'SCHEDULED' && p.amount
          ? p.amount
          : 0),
    0,
  );

  const totalSettledPaymentVolume = user.payments.reduce(
    (total, p) =>
      (total += ["SETTLED"].includes(p.status) && p.amount ? p.amount : 0),
    0,
  );

  const creditUsed = (
    totalTransactionVolume - totalSettledPaymentVolume
  ).toFixed(2);
  const creditUtilization = (creditUsed / user.credit.credit_limit) * 100;
  const currBalance = (
    totalSettledTransactionVolume - totalSettledPaymentVolume
  ).toFixed(2);
  const currBalanceInclPendingPayments = (
    totalSettledTransactionVolume - totalPaymentVolume
  ).toFixed(2);

  const currBalanceInclPendingAndSchedPayments = (
    currBalanceInclPendingPayments - totalScheduledPaymentVolume
  ).toFixed(2);

  const card_status = user.card.activated ? (
    cardStatus === "ACTIVE" ? (
      <span style={{ color: "green", fontWeight: "bold" }}>Card ACTIVE</span>
    ) : (
      <span style={{ color: "red", fontWeight: "bold" }}>Card LOCKED</span>
    )
  ) : (
    <span style={{ color: "red", fontWeight: "bold" }}>
      Card has not been activated yet
    </span>
  );

  const handleCardAction = (payload) => {
    if (cardStatus !== "LOCKED") {
      return freezeUserCard(payload).then((res) => {
        const { status, error } = res.data.freezeUserCard;
        if (status === "OK") {
          setLockReasons([...lockReasons, "admin"]);
          setCardStatus("LOCKED");
        } else {
          console.log("failed to freeze user, ", error);
        }
      });
    }
    return unfreezeUserCard(payload).then((res) => {
      const { status, error } = res.data.unfreezeUserCard;
      if (status === "OK") {
        setLockReasons([]);
        setCardStatus(user.card.activated ? "ACTIVE" : "PENDING");
      } else {
        console.log("failed to unfreeze user, ", error);
      }
    });
  };

  const cardAction = (
    <div>
      <div className="tooltip">
        Card Status: {cardStatus}
        {cardStatus === "LOCKED" && (
          <span className="tooltiptext2 text-sm border">
            <ul className="list-inside list-disc text-left">
              {lockReasons.map((lr, i) => (
                <li key={`lr-${i}`}>{lr}</li>
              ))}
            </ul>
          </span>
        )}
      </div>
      <div>
        <button
          onClick={(e) => {
            openModal(e, "CardActionModal", {
              handleCardAction,
              cardStatus,
              user_id: user.email,
            });
          }}
          className="text-green-primary"
        >
          {`${cardStatus === "LOCKED" ? "Unfreeze" : "Freeze"} card`}
        </button>
      </div>
    </div>
  );

  // const userTags = (user.tags || []).map((t,i) => <span key={`tag-${i}`} className="rounded-md border mx-2 px-2 py-1 border-red-400 text-red-500 font-bold">{t}</span>);
  // if ((user.tags || []).includes("potential_fraud")) {
  //   if (user.metadata && user.metadata.fraud_reason) {
  //     const { fraud_reason } = JSON.parse(user.metadata);
  //     fraud_reason.forEach(fr => {
  //       userTags.push(<span className="rounded-md border mx-2 px-2 py-1 border-red-400 text-red-500 font-bold">{fr}</span>)
  //     })
  //   }
  // }

  const userTags = (user.tags || []).map((t, i) => (
    <div
      key={`tag-${i}`}
      className="rounded-md border mx-2 px-2 py-1 border-red-400 text-red-500 font-bold cursor-pointer"
      onClick={(e) => {
        openModal(e, "TagModal", {
          setUserTags,
          resolveFraudTag,
          user,
          tag: t,
        });
      }}
    >
      {t}
    </div>
  ));

  userTags.push(
    <span
      key="tag-new"
      className="rounded-md border mx-2 px-2 py-1 border-red-400 text-red-500 border-dotted cursor-pointer"
      onClick={(e) => {
        openModal(e, "TagModal", {
          setUserTags,
          resolveFraudTag,
          user,
          tag: null,
        });
      }}
    >
      Add New Tag
    </span>,
  );

  const checkSSN = () => {
    const { ssn_last_four } = user;

    if (CryptoJS.SHA256(last4SSN).toString() === ssn_last_four) {
      setSSNResult(true);
      setShouldShowSSNCheck(true);
      return;
    }

    setSSNResult(false);
    setShouldShowSSNCheck(true);
  };

  const handleAmbassadorClick = async () => {
    let type = AMBASSADOR_TYPE.AMBASSADOR;
    let textToUse = REMOVE_AMBASSADOR;
    if (user.type === AMBASSADOR_TYPE.AMBASSADOR) {
      type = AMBASSADOR_TYPE.NORMAL;
      textToUse = MAKE_AMBASSADOR;
    }

    setAmbassadorButtonDisabled(true);

    const res = await updateAmbassadorStatus({
      variables: {
        email: user.email,
        type,
      },
    });

    const {
      data: {
        updateAmbassadorStatus: { status },
      },
    } = res;

    setAmbassadorButtonDisabled(false);

    if (status === ERROR_STATUS) {
      setErrorMessage(
        "There was an error updating the ambassador status.  Please try again later.",
      );
      return;
    }

    setAmbassadorText(textToUse);
    refetch();
  };

  return (
    <div>
      <div className="mb-2 flex items-center">
        <span className="align-middle text-4xl font-title font-bold mr-5">
          {user.first_name} {user.last_name}
        </span>
        <span>
          <Button
            className="btn-green mt-2 mr-5"
            onClick={async () => {
              await handleAmbassadorClick();
            }}
            disabled={ambassadorButtonDisabled}
          >
            {ambassadorText}
          </Button>
          <div className={errorMessage ? "text-red-600 mb-4 w-96" : "hidden"}>
            {errorMessage}
          </div>
        </span>
      </div>
      <div className="flex items-start">
        {(user.products || []).map((product) => {
          return ProductPill({ product });
        })}
      </div>
      <div className="flex items-start my-2">{userTags}</div>
      <div className="flex flex-row mb-8 md:flex-row">
        <div className="flex mb-4 mx-20 md:mb-0 flex-col md:mx-2">
          <div>
            <div>{card_status}</div>
            <div>{user.email}</div>
            <div>
              <span>Status: </span>
              <span className="font-semibold">
                {(user.completion_status || "unknown").toUpperCase()}
              </span>
            </div>
            <div>
              <span>Autopay Group: </span>
              <span className="font-semibold">
                {(
                  DAYS_OF_WEEK[(user.autopay_day || 0) - 1] || "unknown"
                ).toUpperCase()}
              </span>
            </div>
          </div>
          <div>
            <div>Spending Power: ${user.credit.credit_limit.toFixed(0)}</div>
            <div>Current Balance: ${currBalance}</div>
            <div>
              Current Balance (incl. pending pmts): $
              {currBalanceInclPendingPayments}
            </div>
            <div>
              Current Balance (incl. pending + sched pmts): $
              {currBalanceInclPendingAndSchedPayments}
            </div>
            <div>
              Credit Used: ${creditUsed}
              {creditUsed < 0 ? (
                <span className="px-2">
                  <button
                    onClick={(e) =>
                      openModal(e, "RefundModal", { user, refetch, creditUsed })
                    }
                    className="text-green-primary"
                  >
                    Refund
                  </button>
                </span>
              ) : null}
            </div>
            <div>{`Credit Utilization: ${creditUtilization.toFixed(2)}%`}</div>
          </div>
        </div>
        <div className="flex mb-4 mx-20 md:mb-0 flex-col md:mx-2">
          <div>
            <div>
              Number of transactions:
              {user.transactions.length}
            </div>
            <div className="font-semibold">
              Total Transaction Volume: ${totalTransactionVolume.toFixed(2)}
            </div>
            <div>
              Total Pending Transactions: $
              {totalPendingTransactionVolume.toFixed(2)}
            </div>
            <div>
              Total Settled Transactions: $
              {totalSettledTransactionVolume.toFixed(2)}
            </div>
          </div>
          <div>
            <div>
              Number of payments:
              {user.payments.length}
            </div>
            <div className="font-semibold">
              Total Payment Volume: ${totalPaymentVolume.toFixed(2)}
            </div>
            <div>
              Total Pending Payments: $
              {(totalPaymentVolume - totalSettledPaymentVolume).toFixed(2)}
            </div>
            <div>
              Total Settled Payments: ${totalSettledPaymentVolume.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="flex mb-4 mx-20 md:mb-0 flex-col md:mx-2">
          <div>{cardAction}</div>
          <div>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure to mark this card as lost? A new card will be sent to the shipping address on file",
                  )
                ) {
                  lostCard({
                    variables: {
                      user_id: user.email,
                    },
                  })
                    .then((res) => {
                      console.log("card marked lost");
                      setCardStatus("TERMINATED");
                    })
                    .catch((e) => {
                      console.log("process failed ", e);
                    });
                }
              }}
              className="text-green-primary"
              disabled={user.card.status === "TERMINATED"}
            >
              Mark Card as Lost
            </button>
          </div>
          <div>
            <button
              onClick={(e) => {
                openModal(e, "SetCreditLimitModal", {
                  setCreditLimit,
                  user_id: user.email,
                });
              }}
              className="text-green-primary"
            >
              Set Spending Power
            </button>
          </div>
          <div>
            <button
              onClick={(e) => {
                openModal(e, "IssueCreditModal", {
                  user,
                  refetch,
                });
              }}
              className="text-green-primary"
            >
              Issue a Credit
            </button>
          </div>
          {shouldShowTermination && <TerminationModal user={user} />}
          {/* <div>
            <button
              onClick={ async e => {
                fetchBalances({ variables: { userId: user.email }});
              }}
              className={
                balancesLoading ? "bg-transparent" : "text-green-primary"
              }
              disabled={balancesLoading}
            >
              Fetch Balances
            </button>
            {balancesLoading ? (
              <i className="fas fa-sync fa-spin ml-2" />
            ) : (
              <></>
            )}
          </div> */}
          <div>
            Galileo PRN:
            {user.galileo_prn}
          </div>
          <div>
            <a
              className="text-green-primary"
              href={user.alloy_link}
              target="_blank"
              rel="noreferrer"
            >
              Alloy Profile
            </a>
          </div>
          <div>
            <button
              onClick={(e) => {
                openModal(e, "ManualPaymentModal", {
                  user,
                  defaultPaymentMethod,
                  refetch,
                });
              }}
              className="text-green-primary"
            >
              Manual Payment
            </button>
          </div>
        </div>
        <div className="flex flex-col p-auto font-bold justify-start bg-gray-300 pt-4 pr-4 pl-4 h-48 gap-3 rounded-md shadow ml-8 w-4/12 -mr-40">
          <div className="flex justify-between">
            <span>Card Last Four:</span>
            <div>
              <span className="mr-2">{user.card.last_four_digits}</span>
              <button className="w-16 bg-gray-300" type="button" />
            </div>
          </div>
          <div className="flex justify-between">
            <span>Phone Number:</span>
            <div>
              <span className="mr-2">
                {phoneUpdated ? `${newNum}` : `${user.phone_number}`}
              </span>
              <button
                type="button"
                style={{ outline: 0 }}
                className="btn-green w-16 text-white font-bold rounded-lg outline-0"
                onClick={(e) =>
                  openModal(e, "UpdatePhoneModal", {
                    user,
                    setNewNum,
                    setPhoneUpdated,
                    refetch,
                  })
                }
              >
                Edit
              </button>
            </div>
          </div>
          <div className="flex justify-between">
            <span>Date of Birth:</span>
            <div>
              <span className="cursor-pointer mr-2">
                {dOBUpdated ? `${newDOB}` : formatBirthdateForUI(user.dob)}
              </span>
              <button
                type="button"
                style={{ outline: 0 }}
                className="btn-green w-16 text-white font-bold rounded-lg outline-0"
                onClick={(e) =>
                  openModal(e, "UpdateDOBModal", {
                    user,
                    setNewDOB,
                    setDOBUpdated,
                    refetch,
                  })
                }
              >
                Edit
              </button>
            </div>
          </div>
          {user.ssn_last_four ? (
            <div className="flex items-center justify-between">
              <input
                className="flex border border-gray-400 rounded-btn px-4 py-2 mt-4"
                maxLength={4}
                onChange={(e) => setLast4SSN(e.target.value)}
                placeholder="Enter users last 4 SSN"
                type="text"
                value={last4SSN}
              />
              {shouldShowSSNCheck && (
                <i
                  className={classNames("mt-4 ml-4 text-5xl", {
                    "fa fa-check-square text-green-400": ssnResult,
                    "fa fa-times text-red-400": !ssnResult,
                  })}
                />
              )}
            </div>
          ) : (
            <div>SSN Not on file</div>
          )}
        </div>
      </div>
    </div>
  );
};

const renderPage = (
  user,
  openModal,
  refetch,
  {
    fetchBalances,
    balancesLoading,
    submitDispute,
    addTransactionTags,
    removeTransactionTag,
  },
) => {
  const transactionReport =
    (user.transaction_report && JSON.parse(user.transaction_report)) || {};
  const underwritingDecisionReport =
    (user.underwritingDecisionReport &&
      JSON.parse(user.underwritingDecisionReport)) ||
    {};
  const { id, email, first_name: firstName, last_name: lastName } = user || {};
  const essentialUser = {
    id: Number(id),
    email,
    firstName,
    lastName,
  };
  return (
    <>
      <FraudDataBox user={user} />
      <AssetReportTable
        className=""
        report={user.asset_report}
        currentAccounts={user.bank_accounts}
        transactionReport={transactionReport}
      />
      <UnderwritingDataBox
        assetData={user.asset_report}
        user={user}
        transactionReport={transactionReport}
      />
      <AccountsList
        user={user}
        openModal={openModal}
        refetch={refetch}
        transactionReport={transactionReport}
        fetchBalances={fetchBalances}
        balancesLoading={balancesLoading}
      />
      <UnderwritingDecisionDataBox
        underwritingDecisionReport={underwritingDecisionReport}
      />
      <BankIdentityDataBox user={user} />
      <UserTransactionsTable
        className=""
        transactions={user.transactions}
        user={essentialUser}
        submitDispute={submitDispute}
        addTransactionTags={addTransactionTags}
        removeTransactionTag={removeTransactionTag}
      />
      <UserPaymentsTable
        className=""
        user={user}
        payments={user.payments}
        payment_methods={user.payment_methods}
      />
      <UserScheduledPaymentsTable
        className=""
        user={user}
        payments={user.scheduledPayments}
        payment_methods={user.payment_methods}
      />
      <IpAddressTable user={user} />
    </>
  );
};

const CreditIncreaseBox = ({ user = {}, loading, resolveLimitRequest }) => {
  const { credit } = user;
  const { limit_increase } = credit || {};
  const { last_request, last_increase, last_request_event } = limit_increase;
  const {
    active,
    reason,
    employed,
    employmentDetail,
    income,
    rent,
    desiredLimit,
    social,
    additionalAccountsAdded,
  } = last_request_event || {};

  const [error, setError] = useState(null);

  if (!last_request || !last_request_event) {
    return (
      <div className="mb-5">
        User has not requested any spending power increases
      </div>
    );
  }

  return (
    <div className="mb-5">
      {active ? (
        <div className="font-bold">
          User has an outstanding spending power increase request:
        </div>
      ) : (
        <div>User has no outstanding limit increase request</div>
      )}
      {last_request_event && (
        <table className={`tomo-table w-auto border ${active ? "border-green-300" : ""}`}>
          <tbody>
            <tr>
              <td>Reason:</td>
              <td>{reason}</td>
            </tr>
            <tr>
              <td>Employed:</td>
              <td>{employed}</td>
            </tr>
            <tr>
              <td>Employment Details:</td>
              <td>{employmentDetail || "(none provided)"}</td>
            </tr>
            <tr>
              <td>Income:</td>
              <td>{income}</td>
            </tr>
            <tr>
              <td>Rent:</td>
              <td>{rent}</td>
            </tr>
            <tr>
              <td>Desired Spending Power:</td>
              <td>{desiredLimit}</td>
            </tr>
            <tr>
              <td>Social:</td>
              <td>{social || "(none provided)"}</td>
            </tr>
            <tr>
              <td>Additional Accounts Added:</td>
              <td>{additionalAccountsAdded ? "YES" : "NO"}</td>
            </tr>
          </tbody>
        </table>
      )}
      {active ? (
        <button
          type="button"
          onClick={() => {
            const res = window.confirm(
              "Are you sure you want to resolve this request?",
            );
            if (res) {
              console.log("resolving event");
              setError(null);
              resolveLimitRequest().then((e) => {
                const { data } = e || {};
                const { resolveLimitRequest: rlr } = data || {};
                const { status, message } = rlr || {};
                if (status !== "OK") {
                  setError(message || "Something went wrong");
                }
              });
            }
          }}
          className={`${
            loading ? "text-gray-primary" : "text-green-primary"
          } p-2`}
          disabled={loading}
        >
          Resolve Request
        </button>
      ) : (
        <div>This event has been resolved</div>
      )}
      {loading ? <i className="fas fa-sync fa-spin ml-2" /> : <></>}
      {error ? <div className="text-red-500">Error:{error}</div> : <></>}
    </div>
  );
};

const UserView = ({ modalContext: { openModal }, ...props }) => {
  const searchParams = new URLSearchParams(props.location.search);
  const email =
    (searchParams.get("email") || props.match.params.user)?.toLowerCase();
  const { loading, error, data, refetch } = useQuery(QUERY, {
    variables: { userId: email },
    fetchPolicy: "network-only",
  });
  const [refetching, setRefetching] = useState(false);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [
    fetchBalances,
    { loading: balancesLoading, data: balancesData, error: balancesError },
  ] = useLazyQuery(FETCH_BALANCES, {
    fetchPolicy: "network-only",
    onCompleted: async () => {
      setRefetching(true);
      await refetch();
      setRefetching(false);
    },
  });
  const [resolveLimitRequest] = useMutation(RESOLVE_LIMIT_REQUEST, {
    variables: { userId: email },
    onCompleted: async () => {
      setResolveLoading(true);
      await refetch();
      setResolveLoading(false);
    },
  });

  const [setCreditLimit] = useMutation(SET_CREDIT_LIMIT);
  const [lostCard] = useMutation(LOST_CARD_RESEND);
  const [setUserTags] = useMutation(SET_USER_TAGS);
  const [resolveFraudTag] = useMutation(RESOLVE_FRAUD_TAG);
  const [freezeUserCard] = useMutation(FREEZE_USER_CARD);
  const [unfreezeUserCard] = useMutation(UNFREEZE_USER_CARD);
  const [disputeTransaction] = useMutation(SUBMIT_DISPUTE);
  const [addUserTransactionTags] = useMutation(ADD_USER_TRANSACTION_TAGS);
  const [removeUserTransactionTag] = useMutation(REMOVE_USER_TRANSACTION_TAG);

  if (loading || balancesLoading) {
    console.log("Loading");
    // return 'Loading...';
  }
  if (error) {
    console.log("god:UserView - error:", error.message);
    // return `Error! ${error.message}`;
  }
  if (balancesError) {
    console.log(
      "god:UserView - error fetching balances:",
      balancesError.message,
    );
  }

  console.log("balancesData:", balancesData);

  track("DASHBOARD_LOADED_GOD_USERVIEW");

  const submitDispute = (
    { transactionId, disputedAmount, form },
    success,
    failure,
  ) => {
    console.log("submitting dispute");
    const { email: user_id } = (data || {}).portalUser || {};
    track("ADMIN_DASHBOARD_TRANSACTIONS_DISPUTE_SUBMITTED");
    disputeTransaction({
      variables: {
        user_id,
        transaction_id: transactionId,
        disputed_amount: `${disputedAmount}`,
        form,
      },
      refetchQueries: [
        {
          query: QUERY,
          variables: { userId: email },
        },
      ],
      onCompleted: (data) => {},
    })
      .then(async (res) => {
        console.log("dispute submitted");
        if (res.data.disputeTransaction.status === "ERROR") {
          track("ADMIN_DASHBOARD_TRANSACTIONS_DISPUTE_FAILURE");
          failure();
        } else {
          success();
          track("ADMIN_DASHBOARD_TRANSACTIONS_DISPUTE_SUCCESS");
          console.log("refetching after dispute");
          setRefetching(true);
          await refetch();
          console.log("refetched!");
          setRefetching(false);
        }
      })
      .catch((e) => {
        console.log(e.message);
        failure();
        track("ADMIN_DASHBOARD_TRANSACTIONS_DISPUTE_FAILURE", {
          message: e.message,
        });
        return { status: "ERROR", message: e.message };
      });
  };

  // ADD USER TRANSACITON TAGS (ARRAY OF TAGS)
  const addTransactionTags = ({ transactionId, tags }, success, failure) => {
    console.log("adding tags");
    addUserTransactionTags({
      variables: {
        transaction_id: transactionId,
        tags,
      },
      refetchQueries: [
        {
          query: QUERY,
          variables: { userId: email },
        },
      ],
      onCompleted: (data) => {},
    })
      .then(async (res) => {
        console.log("tags updated successfully");
        if (res.data.addUserTransactionTags.status === "ERROR") {
          console.log(res.data.addTransactionTag.status);
          failure();
        } else {
          console.log("refetching after adding tags");
          setRefetching(true);
          const results = await refetch();
          // SENDS UPDATED TRANSACTION TAGS TO DETAILS COMPONENT
          const updatedResults = results.data.portalUser.transactions.find(
            (key) => key.id === transactionId,
          ).tags;
          success(updatedResults);
          console.log("refetched!");
          setRefetching(false);
        }
      })
      .catch((e) => {
        console.log("error adding transaction tags:", e.message);
        failure();
        return { status: "ERROR", message: e.message };
      });
  };

  // REMOVE USER TRANSACTION TAG
  const removeTransactionTag = ({ transactionId, tag }, success, failure) => {
    console.log("removing tags");
    removeUserTransactionTag({
      variables: {
        transaction_id: transactionId,
        tag,
      },
      refetchQueries: [
        {
          query: QUERY,
          variables: { userId: email },
        },
      ],
      onCompleted: (data) => {},
    })
      .then(async (res) => {
        console.log("tags updated successfully");
        if (res.data.removeUserTransactionTag.status === "ERROR") {
          console.log(res.data.removeTransactionTag.status);
          failure();
        } else {
          console.log("refetching after removing tags");
          setRefetching(true);
          const results = await refetch();
          // SENDS UPDATED TRANSACTION TAGS TO DETAILS COMPONENT
          const updatedResults = results.data.portalUser.transactions.find(
            (key) => key.id === transactionId,
          ).tags;
          success(updatedResults);
          console.log("refetched!");
          setRefetching(false);
        }
      })
      .catch((e) => {
        console.log("error removing transaction tags:", e.message);
        failure();
        return { status: "ERROR", message: e.message };
      });
  };

  return error ? (
    <>Error...</>
  ) : loading ? (
    <>Loading...</>
  ) : (
    <div className="admin-page">
      <UserStats
        user={data.portalUser}
        setCreditLimit={setCreditLimit}
        openModal={openModal}
        lostCard={lostCard}
        fetchBalances={fetchBalances}
        setUserTags={setUserTags}
        resolveFraudTag={resolveFraudTag}
        balancesLoading={balancesLoading || refetching}
        freezeUserCard={freezeUserCard}
        unfreezeUserCard={unfreezeUserCard}
        refetch={refetch}
      />
      <CreditIncreaseBox
        user={data.portalUser}
        loading={resolveLoading}
        resolveLimitRequest={resolveLimitRequest}
      />
      <Notes user={data.portalUser} />
      {renderPage(data.portalUser, openModal, refetch, {
        fetchBalances,
        balancesLoading: balancesLoading || refetching,
        submitDispute,
        addTransactionTags,
        removeTransactionTag,
      })}
    </div>
  );
};

export default compose(withModal)(UserView);
